import { api } from "../api";
import {
  ClassroomEntity,
  CreateClassroomDto,
  UpdateClassroomDto,
  GenerateSelectionPayload,
  GenerationGrid,
  GenerateResult,
  ClassroomProgramEnrollmentView,
  ClassroomProgramEnrollmentResult,
  UpdateClassroomProgramEnrollmentPayload,
  UpdateClassroomTeacherScopeDto,
  ClassroomTeacherScopeResult,
} from "./classrooms.types";
import { MoveClassroomResult } from "../schools/campus.types";

export const getClassrooms = async (): Promise<ClassroomEntity[]> => {
  try {
    const response = await api.get("/classrooms");
    return response.data;
  } catch (error) {
    console.error("Error fetching classrooms:", error);
    throw error;
  }
};

export const getClassroomsBySchoolId = async (schoolId: number): Promise<ClassroomEntity[]> => {
  try {
    const response = await api.get(`/classrooms/school/${schoolId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching classrooms for school ${schoolId}:`, error);
    throw error;
  }
};

export const getClassroomById = async (id: number): Promise<ClassroomEntity> => {
  try {
    const response = await api.get(`/classrooms/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching classroom with id ${id}:`, error);
    throw error;
  }
};

export const createClassroom = async (classroom: CreateClassroomDto): Promise<ClassroomEntity> => {
  try {
    const response = await api.post("/classrooms", classroom);
    return response.data;
  } catch (error) {
    console.error("Error creating classroom:", error);
    throw error;
  }
};

export const updateClassroom = async (id: number, classroom: UpdateClassroomDto): Promise<ClassroomEntity> => {
  try {
    const response = await api.patch(`/classrooms/${id}`, classroom);
    return response.data;
  } catch (error) {
    console.error(`Error updating classroom with id ${id}:`, error);
    throw error;
  }
};

export const deleteClassroom = async (id: number): Promise<void> => {
  try {
    await api.delete(`/classrooms/${id}`);
  } catch (error) {
    console.error(`Error deleting classroom with id ${id}:`, error);
    throw error;
  }
};

// Preview/generate: backend-authoritative resolution shared by both calls
// (design ADR-2) — the frontend never computes custom_code or status.
export const previewGeneration = async (
  schoolId: number,
  payload: GenerateSelectionPayload
): Promise<GenerationGrid> => {
  try {
    const response = await api.post(
      `/classrooms/school/${schoolId}/generate/preview`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error(`Error previewing generation for school ${schoolId}:`, error);
    throw error;
  }
};

export const generateClassrooms = async (
  schoolId: number,
  payload: GenerateSelectionPayload
): Promise<GenerateResult> => {
  try {
    const response = await api.post(
      `/classrooms/school/${schoolId}/generate`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error(`Error generating classrooms for school ${schoolId}:`, error);
    throw error;
  }
};

// Enrollment subsets (design #860 §7): a group<->program pair either enrolls
// every student in the group (all_students=true, the hot/default path) or a
// restricted subset managed via ClassroomProgramStudent rows.
export const getClassroomPrograms = async (
  classroomId: number
): Promise<ClassroomProgramEnrollmentView[]> => {
  try {
    const response = await api.get(`/classrooms/${classroomId}/programs`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching programs for classroom ${classroomId}:`, error);
    throw error;
  }
};

export const addClassroomProgram = async (
  classroomId: number,
  programId: number
): Promise<{ program_id: number; program_title: string; all_students: boolean }> => {
  try {
    const response = await api.post(
      `/classrooms/${classroomId}/programs/${programId}`
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error adding program ${programId} to classroom ${classroomId}:`,
      error
    );
    throw error;
  }
};

export const removeClassroomProgram = async (
  classroomId: number,
  programId: number
): Promise<void> => {
  try {
    await api.delete(`/classrooms/${classroomId}/programs/${programId}`);
  } catch (error) {
    console.error(
      `Error removing program ${programId} from classroom ${classroomId}:`,
      error
    );
    throw error;
  }
};

export const updateClassroomProgramEnrollment = async (
  classroomId: number,
  programId: number,
  payload: UpdateClassroomProgramEnrollmentPayload
): Promise<ClassroomProgramEnrollmentResult> => {
  try {
    const response = await api.put(
      `/classrooms/${classroomId}/programs/${programId}/enrollment`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error updating enrollment for classroom ${classroomId} program ${programId}:`,
      error
    );
    throw error;
  }
};

// Teacher scope (design #860 §7 follow-up, PR-S6): writes a teacher's
// COMPLETE materia scope for one group in a single call — the endpoint is
// per (classroom, teacher), not per (classroom, program), because the
// backend must replace the teacher's whole scope atomically.
export const updateClassroomTeacherScope = async (
  classroomId: number,
  teacherId: number,
  payload: UpdateClassroomTeacherScopeDto
): Promise<ClassroomTeacherScopeResult> => {
  try {
    const response = await api.put(
      `/classrooms/${classroomId}/teachers/${teacherId}/scope`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error updating teacher scope for classroom ${classroomId} teacher ${teacherId}:`,
      error
    );
    throw error;
  }
};

// SCRUM-916: mueve una clase a otro plantel del mismo árbol. El backend
// recalcula el custom_code con la abreviatura del destino, arrastra a los
// alumnos y desconecta a los profes que queden fuera del plantel destino.
export const moveClassroomToCampus = async (
  classroomId: number,
  targetSchoolId: number
): Promise<MoveClassroomResult> => {
  try {
    const response = await api.post(`/classrooms/${classroomId}/move`, {
      targetSchoolId,
    });
    return response.data;
  } catch (error) {
    console.error(`Error moving classroom ${classroomId} to campus:`, error);
    throw error;
  }
};
